import React, { useState, useEffect } from 'react';
import { FormikProps } from 'formik';
import FormSteps from '../FormSteps';
import { useMutation, useLazyQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { REFINE_BIO } from '../../../../../graphql/REFINE_BIO';
import { GENERATE_BIO_TAGS } from '../../../../../graphql/GENERATE_BIO_TAGS';

interface BioStepProps {
  formik: FormikProps<any>;
  goToNextStep: () => void;
  skipStep?: () => void;
}

const BioStep: React.FC<BioStepProps> = ({ formik, goToNextStep, skipStep }) => {
  const [isRefining, setIsRefining] = useState(false);
  const [refinedPreview, setRefinedPreview] = useState<string | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  const [generateTags] = useLazyQuery(GENERATE_BIO_TAGS, {
    onCompleted: (data) => {
      if (data?.generateBioTags) {
        setAvailableTags(data.generateBioTags);
      }
      setIsLoadingTags(false);
    },
    onError: (error) => {
      toast.error(`Failed to generate tags: ${error.message}`);
      setIsLoadingTags(false);
    }
  });

  const [refineBio] = useMutation(REFINE_BIO, {
    onCompleted: (data) => {
      if (data?.refineBio?.refinedText) {
        setRefinedPreview(data.refineBio.refinedText);
      }
      setIsRefining(false);
    },
    onError: () => {
      toast.error('Failed to refine. Please try again.');
      setIsRefining(false);
    }
  });

  useEffect(() => {
    const bioText = formik.values.bio;
    if (!bioText || bioText.trim().length === 0) return;

    const timer = setTimeout(() => {
      setIsLoadingTags(true);
      generateTags({
        variables: {
          bioText,
          userContext: null
        }
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [formik.values.bio, generateTags]);

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleRefine = async () => {
    const originalText = formik.values.bio || '';
    if (!originalText.trim()) {
      toast.info('Please enter some text first');
      return;
    }

    setIsRefining(true);
    await refineBio({
      variables: {
        originalText
      }
    });
  };

  const handleAcceptRefined = () => {
    if (refinedPreview) {
      formik.setFieldValue('bio', refinedPreview);
      toast.success('Refined text applied!');
      setRefinedPreview(null);
    }
  };

  const handleRejectRefined = () => {
    setRefinedPreview(null);
  };

  const handleNext = () => {
    // Save selected tags to formik if any tags were selected
    if (selectedTags.length > 0) {
      formik.setFieldValue('bioTags', selectedTags);
    }
    goToNextStep();
  };

  return (
    <div className="infostep-custom-header">
      <FormSteps
        header="Please provide a short bio about yourself"
        description="Tell your short story."
        nextButton={handleNext}
        skipButton={skipStep}
      >
        <div className="field mb-5 brand-info-field">
          <div className="is-flex">
            <button
              type="button"
              className={`button page-add-project-controls-button has-text-weight-medium is-primary ${isRefining ? 'is-loading' : ''}`}
              onClick={handleRefine}
              disabled={isRefining}
            >
              {isRefining ? '' : 'Refine With AI'}
            </button>
          </div>
          <div className="field-body">
            <textarea
              className="textarea brand-info-textarea"
              name="bio"
              placeholder="Eg. I am a web designer specializing in designing interactive websites. I would love to take my design work to the next level with a motion graphics artist. Coffee lover."
              rows={4}
              value={formik.values.bio || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isRefining}
            />
          </div>
        </div>

        {refinedPreview && (
          <div className="refined-preview-container">
            <div className="refined-preview-card">
              <div className="refined-preview-header is-flex">
                <span className="refined-preview-title">
                  AI Suggestion
                </span>
                <button
                  type="button"
                  onClick={handleRejectRefined}
                  className="refined-preview-close"
                >
                  ×
                </button>
              </div>
              <div className="refined-preview-text">
                {refinedPreview}
              </div>
              <div className="refined-preview-actions is-flex">
                <button
                  type="button"
                  onClick={handleAcceptRefined}
                  className="button-next refined-button"
                >
                  Apply Changes
                </button>
                <button
                  type="button"
                  onClick={handleRejectRefined}
                  className="button-skip refined-button"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {availableTags.length > 0 && (
          <div className="brand-tags-section">
            <div className="ai-tag-text">
              <span>Add more with AI </span>
            </div>

            <div className="brand-tags-container">
              {availableTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  className={`brand-tag ${selectedTags.includes(tag) ? 'selected' : ''}`}
                  onClick={() => toggleTag(tag)}
                  disabled={isLoadingTags || isRefining}
                >
                  <span className="tag-label">{tag}</span>
                  <span className="tag-action-icon">
                    {selectedTags.includes(tag) ? '–' : '+'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </FormSteps>
    </div>
  );
};

export default BioStep;
